/* eslint-disable prettier-vue/prettier */
/*
 * Knowledge Bases Store
 * */
import { defineStore } from 'pinia';
import { ref, reactive, computed, watch } from 'vue';
import { pathOr, hasPath } from 'ramda';
import { useAuthStore } from '@/store/AuthStore';
import { useCollection } from 'vuefire';
import { doc, setDoc, getDoc, collection, deleteDoc } from 'firebase/firestore';
import { fbDb } from '@/firebaseApp';
import { useToast } from 'vue-toastification';
import { useApi } from '@/hooks/useApi';
import { getUUID, pathPut, KB_DOC_VERSION } from '@enegelai/core';
import { KB_TYPE_DEFS } from '@/spec/KbTypeDefs';
import { shake256 } from 'js-sha3';
import { KB_DOC_STATUS } from '@/const';
import { useSettingsStore } from '@/store/SettingsStore';

const EMPTY_KB_SPEC = {
  name: 'New KB',
};

export const useKbStore = defineStore('kb', () => {
  const authStore = useAuthStore();
  const settingsStore = useSettingsStore();
  //await settingsStore.getElasticIndexSpan();
  const isAuditEnabled = computed(() => settingsStore?.tenantSettings?.featureToggles?.kbAuditEnabled || false);

  const tenantId = computed(() => authStore.tenantId);

  const api = useApi();

  // List of all kbs, with subscription - will be updated if any document is changed in the collection
  const knowledgeBases = useCollection(collection(fbDb, `tenants/${tenantId.value}/kb`));

  const kbIdAssigned = ref(null);

  const kbIsNew = ref(false);
  const kbIsChanged = ref(false);

  const kbSpec = ref(reactive({}));
  const kbId = computed(() => kbSpec.value?.id || null);
  const kbName = computed(() => kbSpec.value?.name || null);
  const kbType = computed(() => kbSpec.value?.type || null);
  const kbTags = computed(() => kbSpec.value?.tags || []);

  const kbTypeTitle = computed(() => {
    return kbType.value in KB_TYPE_DEFS ? KB_TYPE_DEFS[kbType.value].title : 'UNKNOWN';
  });

  const kbIcon = computed(() => {
    return kbType.value in KB_TYPE_DEFS ? KB_TYPE_DEFS[kbType.value].icon : 'message-processing-outline';
  });
  // Snapshot for paused state
  const kbDocumentsSnapshot = ref([]);
  const kbDocumentsCollection = computed(() => (kbId.value ? collection(fbDb, `tenants/${tenantId.value}/kb/${kbId.value}/documents`) : null));
  const kbDocumentsRaw = useCollection(kbDocumentsCollection);
  // Flag to pause reactive updates during bulk operations
  const pauseReactiveUpdates = ref(false);

  // This becomes our main reactive documents reference with status
  const kbDocuments = computed(() => {
    // Return snapshot during bulk operations to prevent slow updates
    if (pauseReactiveUpdates.value) {
      return kbDocumentsSnapshot.value;
    }

    if (!kbDocumentsRaw.value || !Array.isArray(kbDocumentsRaw.value)) {
      return [];
    }
    return kbDocumentsRaw.value.map((doc) => {
      return {
        id: doc.id,
        ...doc,
        status: getKbDocumentStatus({ published: doc.published, updated: doc.updated }),
      };
    });
  });

  const contentPublishingEnabled = computed(() => kbSpec.value?.contentPublishingEnabled || true);
  const currentKbDocumentId = ref(null);
  const currentKbDocument = computed(() => (Array.isArray(kbDocuments.value) && currentKbDocumentId.value ? kbDocuments.value.find((x) => x.id === currentKbDocumentId.value) : null));
  const currentKbDocumentTitle = computed(() => (currentKbDocument.value ? currentKbDocument.value?.metadata?.title || null : null));
  const currentKbDocumentName = computed(() => (currentKbDocument.value ? currentKbDocument.value?.name || null : null));
  const currentKbDocumentTags = computed(() => (currentKbDocument.value ? currentKbDocument.value?.metadata?.tags || [] : []));
  const currentKbDocumentSplit = computed(() => (currentKbDocument.value ? currentKbDocument.value?.metadata?.split || 'default' : 'default'));
  const currentKbDocumentPartsCollection = computed(() =>
    currentKbDocumentSplit.value === 'semantic' ? collection(fbDb, `tenants/${tenantId.value}/kb/${kbId.value}/documents/${currentKbDocumentId.value}/parts`) : null,
  );
  const currentKbDocumentSemanticSplitInstructions = computed({
    get: () => currentKbDocument.value?.metadata?.semanticSplitInstructions || '',
    set: (value) => {
      if (!currentKbDocument.value.metadata) {
        currentKbDocument.value.metadata = {};
      }
      currentKbDocument.value.metadata.semanticSplitInstructions = value;
    },
  });
  const currentKbDocumentPartsRaw = useCollection(currentKbDocumentPartsCollection);
  const currentKbDocumentParts = computed(() => {
    if (!currentKbDocumentPartsRaw.value || !Array.isArray(currentKbDocumentPartsRaw.value)) {
      return [];
    }
    return currentKbDocumentPartsRaw.value;
  });
  const currentKbDocumentDoNotSplit = computed(() => (currentKbDocument.value ? currentKbDocument.value?.doNotSplit || false : false));
  const currentKbDocumentContent = ref();
  const currentKbDocumentContentVersion = ref(KB_DOC_VERSION.DRAFT);
  const currentKbDocumentContentVersions = ref({
    [KB_DOC_VERSION.DRAFT]: null,
    [KB_DOC_VERSION.CURRENT]: null,
  });

  const selectedKbDocuments = ref([]);
  const unpublishedKbDocumentCount = computed(() => kbDocuments.value.reduce((acc, x) => (x.status === KB_DOC_STATUS.DRAFT ? acc + 1 : acc), 0));

  const toast = useToast();

  function getKb(kbId) {
    const kb = knowledgeBases.value.find((x) => x.id === kbId);
    return kb ? kb : null;
  }

  function normalizeKbSpec() {
    if (!('id' in kbSpec.value) || !kbSpec.value.id) {
      const id = getUUID();
      setKbSpecProp('id', id);
    }
    if (!('type' in kbSpec.value) || !(kbSpec.value.type in KB_TYPE_DEFS)) {
      setKbSpecProp('type', 'standard');
    }
    const kbType = kbSpec.value?.type || 'standard';
    // Set defaults for all spec props
    const kbTypeDef = KB_TYPE_DEFS[kbType] || null;
    if (kbTypeDef) {
      const props = kbTypeDef?.props || [];
      for (let i = 0; i < props.length; i++) {
        let pName = props[i]?.name;
        let pDef = props[i]?.default;
        // pName could be path in dot notation: prop.childprop ...
        const propPath = pName.split('.');
        const propExists = hasPath(propPath, kbSpec.value);
        //console.log(`Checking: ${propPath}: exists: ${propValue !== null}`);
        if (!propExists) {
          setKbSpecProp(pName, pDef);
        }
      }
    }
  }

  function setKbSpecProp(propPath, value) {
    kbIsChanged.value = true;
    pathPut(kbSpec.value, propPath, value);
  }

  // Can pass array or dot notation path, like "spec.bot.prop"
  function getKbSpecPropGetSet(propPath) {
    const pp = Array.isArray(propPath) ? propPath : propPath.split('.');
    return {
      get() {
        return pathOr(null, pp, kbSpec.value);
      },
      set(newValue) {
        setKbSpecProp(pp, newValue);
      },
    };
  }

  async function createKb(kbType, kbName = 'New Knowledge Base') {
    const newKbSpec = {
      id: getUUID(),
      name: kbName,
      type: kbType,
    };
    kbSpec.value = reactive(newKbSpec);
    normalizeKbSpec();
    kbIsNew.value = true;
    kbIsChanged.value = true;
    return newKbSpec.id;
  }

  // Should be called from Kb editor when we are already editing spec
  // Just updates name and ID
  function cloneKbInPlace(newName = '') {
    const newKbName = newName && newName !== '' ? newName : kbName.value + ' (Copy)';
    const newKbId = getUUID();
    kbSpec.value.id = newKbId;
    kbSpec.value.name = newKbName;
    kbIsNew.value = true;
    kbIsChanged.value = true;
    return newKbId;
  }

  // TODO Revisit this - should be called from Kb list ?
  async function cloneKb(kbSpec) {
    const newKbName = (kbSpec?.name || 'New Knowledge Base') + ' (Copy)';
    const newKbSpec = Object.assign({}, JSON.parse(JSON.stringify(kbSpec)), {
      id: getUUID(),
      name: newKbName,
    });
    kbSpec.value = reactive(newKbSpec);
    normalizeKbSpec();
    kbIsNew.value = true;
    kbIsChanged.value = true;
    return newKbSpec.id;
  }

  async function importKb(newKbSpec) {
    kbSpec.value = reactive(newKbSpec);
    normalizeKbSpec();
    kbIsChanged.value = true;
    return kbSpec.value.id;
  }

  // Load existing or create new empty kb document
  // Save id, so we know current kb id while loading spec
  async function loadKb(id) {
    // TODO double-check and refine for create KB case
    kbIdAssigned.value = id;
    selectedKbDocuments.value = [];
    const emptySpec = Object.assign({ id: id }, EMPTY_KB_SPEC);
    let currentKbSpec = null;
    const kbDocRef = doc(fbDb, `tenants/${tenantId.value}/kb`, id);
    const kbDoc = await getDoc(kbDocRef);
    if (kbDoc.exists()) {
      const kbSpecDoc = await getDoc(doc(kbDocRef, 'versions', 'current'));
      if (!kbSpecDoc.exists()) {
        currentKbSpec = emptySpec;
      } else {
        const specData = kbSpecDoc.data();
        let spec = null;
        try {
          spec = JSON.parse(specData.spec);
        } catch (e) {
          console.log(`KB[${id}]: Failed to parse spec JSON: ${e.message}`);
          spec = emptySpec;
        }
        currentKbSpec = spec;
      }
    } else {
      console.log(`KB[${id}]: Does not exist, assuming new`);
      currentKbSpec = emptySpec;
      kbIsNew.value = true;
    }
    kbSpec.value = reactive(currentKbSpec);
    kbIsChanged.value = false;
    normalizeKbSpec();
  }

  async function addTagToKb(tag) {
    const kbId = kbSpec.value?.id || null;
    if (!kbId) {
      return false; // TODO error
    }
    if (!Array.isArray(kbSpec.value?.tags)) {
      kbSpec.value.tags = [];
    }
    kbSpec.value.tags.push(tag);
    await saveKb();
  }

  // Saves currently loaded KB
  async function saveKb() {
    const kbId = kbSpec.value?.id || null;
    if (!kbId) {
      return false; // TODO error
    }
    try {
      await setDoc(
        doc(fbDb, `tenants/${tenantId.value}/kb`, kbId),
        {
          name: kbSpec.value?.name || '',
          description: kbSpec.value?.description || '',
          type: kbSpec.value?.type || '',
          tags: kbSpec.value?.tags || [],
          updated: Date.now(),
          updatedBy: authStore.displayName,
        },
        { merge: true },
      );
    } catch (e) {
      console.log(`KB[${kbId}]: Error writing document: ${e.message}`);
      return false;
    }

    const kbSpecJSON = JSON.stringify(kbSpec.value);
    try {
      await setDoc(doc(fbDb, `tenants/${tenantId.value}/kb/${kbId}/versions`, 'current'), {
        spec: kbSpecJSON,
      });
    } catch (e) {
      console.log(`KB[${kbId}]: Error writing spec: ${e.message}`);
      return false;
    }
    kbIsNew.value = false;
    kbIsChanged.value = false;
    return true;
  }

  async function deleteKb(kbId) {
    try {
      // TODO Delete versions collection ?
      await deleteDoc(doc(fbDb, `tenants/${tenantId.value}/kb`, kbId));
    } catch (e) {
      console.log(`KB[${kbId}]: Error deleting KB: ${e.message}`);
      return false;
    }
    return true;
  }

  async function loadKbDocument(kbId, kbDocumentId) {
    if (kbIdAssigned.value !== kbId) {
      console.log(`Error: kbId mismatch: ${kbIdAssigned.value} !== ${kbId}`);
      return;
    }
    if (currentKbDocumentId.value === kbDocumentId) {
      return;
    }
    currentKbDocumentId.value = kbDocumentId;
    const res = await api.getKbDocumentContent({
      kbId: kbId,
      documentId: kbDocumentId,
    });
    if (res.success) {
      currentKbDocumentContentVersions.value = res.data;
    } else {
      currentKbDocumentContentVersions.value = {};
      toast.error(`Unable to load document: ${res.error}`);
    }
  }

  async function swapCurrentKbDocumentContent(contentVersion) {
    currentKbDocumentContent.value = currentKbDocumentContentVersions.value[contentVersion];
    currentKbDocumentContentVersion.value = contentVersion;
    return currentKbDocumentContent.value;
  }

  async function unloadKbDocument() {
    currentKbDocumentId.value = null;
    currentKbDocumentContent.value = null;
  }

  function setTagsForCurrentKbDocument(tags) {
    if (!currentKbDocument.value) {
      return;
    }
    if (!currentKbDocument.value?.metadata) {
      currentKbDocument.value.metadata = {};
    }
    currentKbDocument.value.metadata.tags = tags;
  }

  function setSplitForCurrentKbDocument(splitMode) {
    if (!currentKbDocument.value) {
      return;
    }
    if (!currentKbDocument.value?.metadata) {
      currentKbDocument.value.metadata = {};
    }
    currentKbDocument.value.metadata.split = splitMode;
  }

  function setDoNotSplitForCurrentKbDocument(val) {
    if (!currentKbDocument.value) {
      return;
    }
    currentKbDocument.value.doNotSplit = val;
  }

  async function saveCurrentKbDocument() {
    let published = currentKbDocument.value?.published;
    if (!published) {
      // for docs created before we started tracking published date
      published = currentKbDocument.value?.updated;
    }
    const res = await api.setKbDocumentContent({
      kbId: kbId.value,
      documentId: currentKbDocumentId.value,
      contentVersions: currentKbDocumentContentVersions.value,
      documentProps: {
        metadata: currentKbDocument.value?.metadata || {
          tags: [],
          split: 'default',
        },
      },
      published: published,
      updated: Date.now(),
      updatedBy: authStore.displayName,
    });
    if (!res.success) {
      // ???
      toast.error(`Error saving document: ${res.error}`);
    }
    return res;
  }

  async function publishCurrentKbDocument() {
    const result = await api.publishKbDocument({
      kbId: kbId.value,
      documentId: currentKbDocumentId.value,
      publishedBy: authStore.displayName,
    });
    if (!result.success) {
      toast.error(`publishCurrentKbDocument: Error - ${result.error}`);
    } else {
      currentKbDocumentContentVersions.value[KB_DOC_VERSION.CURRENT] = currentKbDocumentContentVersions.value[KB_DOC_VERSION.DRAFT];
    }
    return result;
  }

  async function publishKbDocument(kbDocument) {
    const result = await api.publishKbDocument({
      kbId: kbId.value,
      documentId: kbDocument.id,
      publishedBy: authStore.displayName,
    });
    if (!result.success) {
      console.error(`publishKbDocument[${kbDocument.id}]: Error - ${result.error}`);
    }
    return result;
  }

  function getDocumentInfoByMetadata(metadata) {
    if (!Array.isArray(kbDocuments.value) || kbDocuments.value.length <= 0) {
      return {};
    }
    const id = metadata?.id || null;
    const source = metadata?.source || null;
    if (id) {
      const dI = kbDocuments.value.find((x) => x.id === id);
      return dI ? dI : {};
    } else if (source) {
      const dI = kbDocuments.value.find((x) => x?.metadata?.source === source);
      return dI ? dI : {};
    } else {
      return {};
    }
  }

  // Create new KB document
  // TODO refine parameters - meta, original content, etc
  async function createKbDocument(documentName, documentContent, documentOriginalContent = null, documentOriginalMimeType = null, documentMetadata = {}) {
    updateCurrentKbDocumentContent(KB_DOC_VERSION.DRAFT, documentContent);
    updateCurrentKbDocumentContent(KB_DOC_VERSION.CURRENT, 'REPLACE WITH YOUR CONTENT\n');
    const res = await api.createKbDocument({
      kbId: kbId.value,
      documentName: documentName,
      contentVersions: currentKbDocumentContentVersions.value,
      documentOriginalContent: documentOriginalContent,
      documentOriginalMimeType: documentOriginalMimeType,
      documentMetadata: documentMetadata,
      updatedBy: authStore.displayName,
    });
    return res;
  }

  async function exportKbDocuments(documentIds) {
    const res = await api.exportKbDocuments({
      kbId: kbId.value,
      documentIds: documentIds,
    });

    if (res.success) {
      if (res.mimeType === 'application/gzip') {
        res.data = decodeBase64(res.data);
        console.log(`Decoded KB documents export:${res.data.length} bytes, mime:${res.mimeType}`);
      }
    }

    return res;
  }

  /**
   * Decodes a base64 string into a Uint8Array
   * @param {string} base64Data - The base64 encoded string to decode
   * @returns {Uint8Array} The decoded Uint8Array
   */
  function decodeBase64(base64Data) {
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * Imports KB documents from export data
   * @param {ArrayBuffer | string} data - The base64 encoded string to import
   * @param {{defalted: boolean}} options - Whether to override existing documents
   * @returns {Promise<Object>} The response from the API
   */
  async function importKbDocuments(data, options = {}) {
    // check if data is ArrayBuffer, if so convert to base64
    if (data instanceof ArrayBuffer) {
      data = encodeBase64(data);
    }

    options.updatedBy = authStore.currentUser.displayName;
    const res = await api.importKbDocuments(
      {
        kbId: kbId.value,
        data: data,
      },
      options,
    );
    return res;
  }

  /**
   * Encodes a Uint8Array into a base64 string
   * @param {ArrayBuffer | Uint8Array} data - The data to encode
   * @returns {string} The base64 encoded string
   */
  function encodeBase64(data) {
    // check if data is ArrayBuffer, if so convert to Uint8Array
    if (data instanceof ArrayBuffer) {
      data = new Uint8Array(data);
    }
    const chunkSize = 0x8000; // 32768
    let binaryString = '';
    for (let i = 0; i < data.length; i += chunkSize) {
      binaryString += String.fromCharCode(...data.subarray(i, i + chunkSize));
    }
    return btoa(binaryString);
  }

  async function deleteKbDocument(documentId) {
    const res = await api.deleteKbDocument({
      kbId: kbId.value,
      documentId: documentId,
    });
    return res;
  }

  async function bulkDeleteKbDocuments(documentIds, options = {}) {
    // Take snapshot and pause reactive updates
    kbDocumentsSnapshot.value = [...kbDocuments.value];
    const originalCount = kbDocumentsSnapshot.value.length;
    pauseReactiveUpdates.value = true;
    
    try {
      const res = await api.bulkDeleteKbDocuments({
        kbId: kbId.value,
        documentIds: documentIds,
        options: options,
      });

      // Calculate expected final count from API response
      const actuallyDeleted = res.success && res.data ? res.data.documentsDeleted || 0 : 0;
      const expectedFinalCount = originalCount - actuallyDeleted;

      if (actuallyDeleted === 0) {
        // No deletions, resume immediately
        pauseReactiveUpdates.value = false;
        return res;
      }

      console.log(`Bulk delete: monitoring for completion (${originalCount} -> ${expectedFinalCount})`);

      // Watch for completion using Firebase's real-time updates
      const unsubscribe = watch(
        () => kbDocumentsRaw.value?.length,
        (currentCount) => {
          if (currentCount !== undefined && currentCount <= expectedFinalCount) {
            console.log(`Bulk delete completed: ${originalCount} -> ${currentCount}`);
            pauseReactiveUpdates.value = false;
            unsubscribe();
          }
        },
        { immediate: true }
      );

      // Safety timeout (10s max)
      setTimeout(() => {
        if (pauseReactiveUpdates.value) {
          console.log('Safety timeout: resuming after 10s');
          pauseReactiveUpdates.value = false;
          unsubscribe();
        }
      }, 10000);

      return res;
    } catch (error) {
      pauseReactiveUpdates.value = false;
      throw error;
    }
  }

  async function updateKbDataSources(options) {
    const res = await api.updateKbDataSources({
      kbId: kbId.value,
      options: options,
    });
    return res;
  }

  function getHashID(str, len = 10) {
    const id = shake256(str, len * 8); // Convert bytes to bits
    return id;
  }

  function updateCurrentKbDocumentContent(version, content) {
    currentKbDocumentContent.value = content;
    currentKbDocumentContentVersion.value = version;
    currentKbDocumentContentVersions.value[version] = content;
  }

  /**
   * Returns status of the document: published or draft
   * @param {Object} doc - KB document
   * @returns {String} status
   */
  function getKbDocumentStatus({ published, updated }) {
    if (published === undefined || published === null) {
      // for docs created before we started tracking published date
      return KB_DOC_STATUS.PUBLISHED;
    }

    if (updated > published) {
      return KB_DOC_STATUS.DRAFT;
    } else {
      return KB_DOC_STATUS.PUBLISHED;
    }
  }

  // ACTIONS // ////////////////////////////////////////////////

  // Called before saving KB document.
  // Observers can subscribe and prepare document for saving
  async function beforeSaveKbDocument() {
    // verify metadata fields and correct them
    if (currentKbDocument.value && currentKbDocument.value.metadata) {
      for (const key in currentKbDocument.value.metadata) {
        const value = currentKbDocument.value.metadata[key];
        if (typeof value == 'number' && (isNaN(value) || !isFinite(value))) {
          currentKbDocument.value.metadata[key] = 0;
        }
      }
    }

    console.log(`kbStore: beforeSaveKbDocument executed`);
    return null;
  }

  return {
    knowledgeBases,
    kbSpec,
    kbId,
    kbName,
    kbType,
    kbTypeTitle,
    kbTags,
    kbIcon,
    kbDocuments,
    kbIsNew,
    kbIsChanged,
    contentPublishingEnabled,
    currentKbDocumentId,
    currentKbDocument,
    currentKbDocumentTitle,
    currentKbDocumentName,
    currentKbDocumentContent,
    currentKbDocumentContentVersion,
    currentKbDocumentTags,
    currentKbDocumentSplit,
    currentKbDocumentDoNotSplit,
    currentKbDocumentParts,
    currentKbDocumentSemanticSplitInstructions,
    selectedKbDocuments,
    unpublishedKbDocumentCount,
    getKb,
    createKb,
    cloneKb,
    cloneKbInPlace,
    importKb,
    loadKb,
    setKbSpecProp,
    getKbSpecPropGetSet,
    addTagToKb,
    saveKb,
    deleteKb,
    loadKbDocument,
    unloadKbDocument,
    setTagsForCurrentKbDocument,
    setSplitForCurrentKbDocument,
    setDoNotSplitForCurrentKbDocument,
    saveCurrentKbDocument,
    publishCurrentKbDocument,
    publishKbDocument,
    getDocumentInfoByMetadata,
    createKbDocument,
    deleteKbDocument,
    bulkDeleteKbDocuments,
    updateKbDataSources,
    beforeSaveKbDocument,
    updateCurrentKbDocumentContent,
    swapCurrentKbDocumentContent,
    getHashID,
    exportKbDocuments,
    importKbDocuments,
    isAuditEnabled,
    pauseReactiveUpdates,
  };
});
